import { useState } from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Checkbox from '@mui/joy/Checkbox'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Stack from '@mui/joy/Stack'
import Typography from '@mui/joy/Typography'
import {
    LOADS_PROMO_TAB_LABELS,
    LOADS_PROMO_TABS,
    PROMO_CARD_POSITION_MAX,
    PROMO_CARD_SUBTITLE_MAX,
    PROMO_CARD_TITLE_MAX,
    type LoadsPromoTab,
    promoPageDeeplink,
} from '@numeo/types'
import { apiErrorMessage, mobileHomeLayoutApi } from '../../../../api/mobileHomeLayout'

interface Props {
    props: Record<string, unknown>
    onText: (key: 'title' | 'subtitle' | 'linkUrl' | 'iconUrl', value: string) => void
    onPosition: (position: number | undefined) => void
    onTabs: (tabs: LoadsPromoTab[] | undefined) => void
    onError: (message: string) => void
}

/** Fields of a Loads promo card (Figma Spot 10475:112409): icon, title, subtitle, link, where it sits, which tabs. */
export function PromoCardFields({ props, onText, onPosition, onTabs, onError }: Props) {
    const [uploading, setUploading] = useState(false)
    const text = (key: string) => (typeof props[key] === 'string' ? (props[key] as string) : '')
    const position = typeof props.position === 'number' ? props.position : undefined
    const tabs = Array.isArray(props.tabs) ? (props.tabs as LoadsPromoTab[]) : []

    async function uploadIcon(event: React.ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        setUploading(true)
        try {
            onText('iconUrl', await mobileHomeLayoutApi.uploadPromoImage(file))
        } catch (err) {
            onError(`Upload failed: ${apiErrorMessage(err)}`)
        } finally {
            setUploading(false)
        }
    }

    const toggleTab = (tab: LoadsPromoTab, checked: boolean) => {
        const current: readonly LoadsPromoTab[] = tabs.length ? tabs : LOADS_PROMO_TABS
        const next = checked ? [...current, tab] : current.filter((t) => t !== tab)
        // A card must show somewhere; to hide it everywhere, switch the row off.
        if (next.length === 0) return
        // Every tab ticked is stored as no list, so a tab added later shows the card too.
        onTabs(next.length < LOADS_PROMO_TABS.length ? LOADS_PROMO_TABS.filter((t) => next.includes(t)) : undefined)
    }

    return (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        bgcolor: '#2454FF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                    }}
                >
                    {text('iconUrl') ? <Box component="img" src={text('iconUrl')} alt="" sx={{ width: 12, height: 20, objectFit: 'contain' }} /> : null}
                </Box>
                <Button size="sm" variant="outlined" component="label" loading={uploading}>
                    {text('iconUrl') ? 'Replace icon' : 'Upload icon'}
                    <input type="file" accept="image/png,image/webp" hidden onChange={(e) => void uploadIcon(e)} />
                </Button>
                {text('iconUrl') ? (
                    <Button size="sm" variant="plain" color="neutral" onClick={() => onText('iconUrl', '')}>
                        Remove
                    </Button>
                ) : null}
                <Typography level="body-xs" color="neutral">
                    White mark on a transparent PNG, drawn on the blue tile. Optional.
                </Typography>
            </Stack>
            <FormControl size="sm">
                <FormLabel>Title</FormLabel>
                <Input
                    size="sm"
                    value={text('title')}
                    onChange={(e) => onText('title', e.target.value)}
                    slotProps={{ input: { maxLength: PROMO_CARD_TITLE_MAX } }}
                />
            </FormControl>
            <FormControl size="sm">
                <FormLabel>Subtitle</FormLabel>
                <Input
                    size="sm"
                    value={text('subtitle')}
                    onChange={(e) => onText('subtitle', e.target.value)}
                    slotProps={{ input: { maxLength: PROMO_CARD_SUBTITLE_MAX } }}
                />
            </FormControl>
            <FormControl size="sm">
                <FormLabel>Link</FormLabel>
                <Input
                    size="sm"
                    placeholder={`${promoPageDeeplink('basicblock')} or https://…`}
                    value={text('linkUrl')}
                    onChange={(e) => onText('linkUrl', e.target.value.trim())}
                />
            </FormControl>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                <FormControl size="sm" sx={{ width: 160 }}>
                    <FormLabel>After load #</FormLabel>
                    <Input
                        size="sm"
                        type="number"
                        value={position === undefined ? '' : String(position)}
                        onChange={(e) => onPosition(e.target.value === '' ? undefined : Number(e.target.value))}
                        slotProps={{ input: { min: 1, max: PROMO_CARD_POSITION_MAX } }}
                    />
                    <FormHelperText>1 = after the first load. Skipped when fewer loads show.</FormHelperText>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Tabs</FormLabel>
                    <Stack direction="row" spacing={1.5}>
                        {LOADS_PROMO_TABS.map((tab) => (
                            <Checkbox
                                key={tab}
                                size="sm"
                                label={LOADS_PROMO_TAB_LABELS[tab]}
                                checked={tabs.length === 0 || tabs.includes(tab)}
                                onChange={(e) => toggleTab(tab, e.target.checked)}
                            />
                        ))}
                    </Stack>
                </FormControl>
            </Stack>
        </Stack>
    )
}
